import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useConfirmDialog } from "@/context/ConfirmDialogContext";

type NavigateTo = { to: string; replace?: boolean };

type GuardEntry = {
  id: string;
  registeredAt: number;
  getIsActive: () => boolean;
  getTitle: () => string;
  getMessage: () => string;
  isDangerous: boolean;
  onConfirm?: () => void | Promise<void>;
  navigateTo?: NavigateTo;

  blockPopstate: boolean;
  blockBeforeUnload: boolean;
  clickScope: "all" | "sidebar";
};

const guards = new Map<string, GuardEntry>();
let registerCounter = 0;

let listenerAttached = false;
let dialogOpen = false;
let ignoreNextPop = false;

let lastIdx =
  typeof (window.history.state as any)?.idx === "number"
    ? (window.history.state as any).idx
    : 0;

let lastStableUrl =
  window.location.pathname + window.location.search + window.location.hash;

const makeId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `guard_${Math.random().toString(36).slice(2)}_${Date.now()}`;

const getUrl = () =>
  window.location.pathname + window.location.search + window.location.hash;

// ✅ helper: avoid unnecessary navigate() on iOS (reduces jank)
const samePath = (a: string, b: string) => a === b;

// iOS Safari detection (good enough for this use-case)
const isIOSSafari = () => {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isWebKit = /WebKit/.test(ua);
  const isCriOS = /CriOS/.test(ua);
  const isFxiOS = /FxiOS/.test(ua);
  return isIOS && isWebKit && !isCriOS && !isFxiOS;
};

const ensureBufferState = (navigate: ReturnType<typeof useNavigate>) => {
  const st: any = window.history.state || {};
  const usr = st?.usr || {};

  if (usr.__nav_guard__) return;

  navigate(getUrl(), {
    replace: false,
    state: { ...usr, __nav_guard__: true },
  });

  setTimeout(() => {
    const st2: any = window.history.state || {};
    if (typeof st2.idx === "number") lastIdx = st2.idx;
    lastStableUrl = getUrl();
  }, 0);
};

const attachOnce = (
  showConfirm: (
    title: string,
    message: string,
    onConfirm: () => void,
    isDangerous?: boolean,
    onCancel?: () => void
  ) => void,
  closeConfirm: () => void,
  navigate: ReturnType<typeof useNavigate>
) => {
  if (listenerAttached) return;
  listenerAttached = true;

  window.addEventListener("popstate", () => {
    const st: any = window.history.state || {};
    const usr = st?.usr || {};
    const idx = typeof st.idx === "number" ? st.idx : lastIdx;

    // URL the browser is trying to go to
    const attemptedUrl = getUrl();

    // 1) ignore our own synthetic pop
    if (ignoreNextPop) {
      ignoreNextPop = false;
      lastIdx = idx;
      lastStableUrl = attemptedUrl;
      return;
    }

    // publish modal allowances
    if (usr.__publish_modal__ || usr.__publish_open__) {
      lastIdx = idx;
      lastStableUrl = attemptedUrl;
      return;
    }

    const active = Array.from(guards.values()).filter(
      (g) => g.getIsActive() && g.blockPopstate
    );

    if ((usr.__publish_modal__ || usr.__publish_open__) && active.length === 0) {
      lastIdx = idx;
      lastStableUrl = attemptedUrl;
      return;
    }

    if (active.length === 0) {
      lastIdx = idx;
      lastStableUrl = attemptedUrl;
      return;
    }

    // ✅ IMPORTANT FIX:
    // If dialog is already open and user presses back again,
    // we must re-trap (restore stable) again — never just "return".
    if (dialogOpen) {
      if (isIOSSafari()) {
        const stNow: any = window.history.state || {};
        window.history.pushState(stNow, "", lastStableUrl);

        // ✅ If router is already on stable url, don't force navigate (less jank)
        if (!samePath(getUrl(), lastStableUrl)) {
          requestAnimationFrame(() => {
            navigate(lastStableUrl, { replace: true });
          });
        }
      } else {
        ignoreNextPop = true;
        window.history.go(1);
      }
      return;
    }

    // Choose revert method
    const useIOSRestore = isIOSSafari();

    if (useIOSRestore) {
      const stNow: any = window.history.state || {};
      window.history.pushState(stNow, "", lastStableUrl);

      if (!samePath(getUrl(), lastStableUrl)) {
        requestAnimationFrame(() => {
          navigate(lastStableUrl, { replace: true });
        });
      }
    } else {
      const direction = idx < lastIdx ? -1 : idx > lastIdx ? 1 : -1;

      ignoreNextPop = true;
      window.history.go(-direction);
    }

    dialogOpen = true;

    const newest = active.reduce(
      (best, g) => (g.registeredAt > best.registeredAt ? g : best),
      active[0]
    );

    showConfirm(
      newest.getTitle(),
      newest.getMessage(),
      async () => {
        try {
          for (const g of active) {
            if (g.onConfirm) await g.onConfirm();
          }
        } finally {
          closeConfirm();
          dialogOpen = false;

          if (newest.navigateTo) {
            const { to, replace } = newest.navigateTo;
            navigate(to, { replace: !!replace });

            setTimeout(() => {
              const st2: any = window.history.state || {};
              if (typeof st2.idx === "number") lastIdx = st2.idx;
              lastStableUrl = getUrl();
            }, 0);
          } else {
            navigate(attemptedUrl, { replace: true });

            setTimeout(() => {
              const st2: any = window.history.state || {};
              if (typeof st2.idx === "number") lastIdx = st2.idx;
              lastStableUrl = getUrl();
            }, 0);
          }
        }
      },
      newest.isDangerous,
      () => {
        closeConfirm();
        dialogOpen = false;
      }
    );
  });
};

interface UseNavigationGuardOptions {
  isActive: boolean;
  title?: string;
  message?: string;
  isDangerous?: boolean;
  onConfirm?: () => void | Promise<void>;
  navigateTo?: NavigateTo;
  onBack?: () => void | Promise<void>;

  blockBrowserNavigation?: boolean;
  blockBeforeUnload?: boolean;
  clickScope?: "all" | "sidebar";
}

export const useNavigationGuard = ({
  isActive,
  title,
  message,
  isDangerous = false,
  onConfirm,
  navigateTo,

  blockBrowserNavigation = true,
  blockBeforeUnload: blockBeforeUnloadOpt = true,
  clickScope = "all",
}: UseNavigationGuardOptions) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showConfirm, closeConfirm } = useConfirmDialog();

  const bufferedRef = useRef(false);
  const idRef = useRef<string>(makeId());
  const lastUrlRef = useRef<string>("");

  useEffect(() => {
    if (!blockBrowserNavigation) return;
    if (!isActive) return;

    const url = getUrl();

    if (lastUrlRef.current !== url) {
      lastUrlRef.current = url;
      bufferedRef.current = false;
    }

    if (!bufferedRef.current) {
      ensureBufferState(navigate);
      bufferedRef.current = true;
    }
  }, [isActive, blockBrowserNavigation, navigate]);

  useEffect(() => {
    if (!blockBeforeUnloadOpt) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isActive) return;
      e.preventDefault();
      e.returnValue =
        message ||
        t("unsaved_changes_warning") ||
        "You have unsaved changes. Are you sure you want to leave?";
      return e.returnValue;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isActive, message, t, blockBeforeUnloadOpt]);

  useEffect(() => {
    const id = idRef.current;

    const entry: GuardEntry = {
      id,
      registeredAt: ++registerCounter,
      getIsActive: () => isActive,
      getTitle: () => title || t("confirm_navigation") || "Confirm Navigation",
      getMessage: () =>
        message ||
        t("unsaved_changes_warning") ||
        "You have unsaved changes. Are you sure you want to leave?",
      isDangerous,
      onConfirm,
      navigateTo,

      blockPopstate: blockBrowserNavigation,
      blockBeforeUnload: blockBeforeUnloadOpt,
      clickScope,
    };

    guards.set(id, entry);
    attachOnce(showConfirm as any, closeConfirm, navigate);

    return () => {
      guards.delete(id);
    };
  }, [
    isActive,
    title,
    message,
    isDangerous,
    onConfirm,
    navigateTo,
    blockBrowserNavigation,
    blockBeforeUnloadOpt,
    clickScope,
    t,
    showConfirm,
    closeConfirm,
    navigate,
  ]);
};

export const getActiveGuardsSnapshot = () =>
  Array.from(guards.values()).filter((g) => g.getIsActive());
